import { type DBSchema, type IDBPDatabase, openDB } from 'idb';
import type { BurfyDocument } from '@burfy/docs-core';
import type { StoredAsset } from '@burfy/docs-core';
import type { LearningEntry } from '@burfy/input-tools';
import type { UploadedFontRecord } from '../fonts/local-font-provider.ts';

/**
 * The browser database.
 *
 * One connection definition shared by every store, so a version bump is
 * declared in a single place and two stores can never disagree about the
 * schema — which, with IndexedDB, means one of them blocking the other's
 * upgrade forever.
 */

export const DEFAULT_DB_NAME = 'burfy-docs';

/**
 * 1 — documents
 * 2 — learned transliteration choices
 * 3 — image assets
 * 4 — uploaded fonts
 */
export const DB_VERSION = 4;

export const DOCUMENTS_STORE = 'documents';
export const LEARNING_STORE = 'learning';
export const ASSETS_STORE = 'assets';
export const FONTS_STORE = 'fonts';

export interface LearningRecord {
  key: string;
  counts: LearningEntry;
}

export interface BurfyDocsDB extends DBSchema {
  documents: {
    key: string;
    value: BurfyDocument;
    indexes: { 'by-updatedAt': string };
  };
  learning: {
    key: string;
    value: LearningRecord;
  };
  assets: {
    key: string;
    /**
     * `storedAt` is local to this implementation, not part of `StoredAsset`.
     *
     * The sweep needs it to leave very recent assets alone: another tab can
     * have imported one seconds ago and not yet autosaved the reference to it,
     * and deleting that would break an image in a document nobody has touched.
     * Absent on records written before this existed, which is the right
     * answer for them — they are old by definition.
     */
    value: StoredAsset & { storedAt?: number };
  };
  fonts: {
    key: string;
    value: UploadedFontRecord;
  };
}

export function openBurfyDocsDb(
  name: string = DEFAULT_DB_NAME,
): Promise<IDBPDatabase<BurfyDocsDB>> {
  return openDB<BurfyDocsDB>(name, DB_VERSION, {
    /**
     * Each step is guarded by the version it was introduced in, and steps are
     * cumulative. Creating stores unconditionally works exactly once — on a
     * database that already exists it throws, and the app stops opening at all.
     */
    upgrade(db, oldVersion) {
      if (oldVersion < 1) {
        const documents = db.createObjectStore(DOCUMENTS_STORE, { keyPath: 'id' });
        documents.createIndex('by-updatedAt', 'meta.updatedAt');
      }

      if (oldVersion < 2) {
        db.createObjectStore(LEARNING_STORE, { keyPath: 'key' });
      }

      if (oldVersion < 3) {
        db.createObjectStore(ASSETS_STORE, { keyPath: 'id' });
      }

      if (oldVersion < 4) {
        db.createObjectStore(FONTS_STORE, { keyPath: 'family' });
      }
    },
  });
}
